import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { format, intlFormatDistance } from 'date-fns';
import { de } from 'date-fns/locale';
import { Mongo } from 'meteor/mongo';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { TranscribeJobs } from 'server/shared/collections/transcribeJobs';
import { groupTranscribeUserKey } from 'server/shared/helpers/groupTranscribeUserKey';
import { SearchTypes } from 'server/shared/helpers/jobs-query/SearchTypes';
import { Customer } from 'server/shared/models/customer';
import { Invitee } from 'server/shared/models/invitee';
import { JobDepartment } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { PoolerType } from 'server/shared/models/poolerType';
import { RoleEnum } from 'server/shared/models/role.enum';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { TranscribeJob } from 'server/shared/models/transcribeJob';
import { ToastService } from 'src/app/services/toast/toast.service';
import { queryParam$ } from 'src/helpers/queryParam$';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { replaceTranslationVariables } from 'src/helpers/translationHelpers';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { TranslationService } from '../../services/translation.service';
import { UserService } from '../../services/user.service';
import { SubscriptionService } from '../../shared/subscription.service';
import { getJobsRangeQuery } from '../getJobsRangeQuery';
import {
  getTranscribeJobSearchQuery,
  TranscribeJobListSearchInFieldEnum,
} from '../getTranscribeJobSearchQuery';

const LEGACY_DEPARTMENT_SEARCH_TYPE = 'DEPARTMENT';

function normalizeTranscribeJobListSearchType(
  searchType?: string | null,
): TranscribeJobListSearchInFieldEnum {
  if (!searchType || searchType === LEGACY_DEPARTMENT_SEARCH_TYPE) {
    return TranscribeJobListSearchInFieldEnum.ALL;
  }
  return searchType as TranscribeJobListSearchInFieldEnum;
}

function normalizeTranscribeJobListSavedSearchType(
  searchType?: string | null,
): string | null {
  if (!searchType || searchType === LEGACY_DEPARTMENT_SEARCH_TYPE) {
    return null;
  }
  return searchType;
}

function normalizeTranscribeJobListSavedSearchText(
  searchType?: string | null,
  searchText?: string | null,
): string | null {
  if (searchType === LEGACY_DEPARTMENT_SEARCH_TYPE) {
    return null;
  }
  return searchText || null;
}

export type TranscribeJobListTab = {
  label: string;
  isGlobalLabel?: boolean;
  value: string;
  svgIcon?: string;
};

export type TranscribeJobUser = {
  groupKey: string;
  userId?: string;
  personalData: Invitee['personalData'];
  customer: TranscribeJob['customer'];
  jobs: Array<{
    transcribeJob: TranscribeJob;
    bookedAtInfo?: string;
    cancelledAtInfo?: string;
  }>;
  displayedName: string;
  poolerType?: PoolerType;
};

export type TranscribeJobDepartment = {
  department: JobDepartment;
  organisation: { id: string; name: string };
  users: Array<TranscribeJobUser>;
  collapsed: boolean;
};

export type TranscribeJobMonth = {
  month: string;
  departments: Array<TranscribeJobDepartment>;
  collapsed: boolean;
};

export type MonthCollapsedStateMap = Map<
  string,
  { collapsed: boolean; departments: Map<string, boolean> }
>;

export type TranscribeListState = {
  months: TranscribeJobMonth[];
  activeTabId: string;
  loading: boolean;
};

export type TranscribeJobsData = {
  transcribeJobs: TranscribeJob[];
  activeTabId: string;
  loading: boolean;
};

function mapByKey<T, K>(items: T[], keyFn: (item: T) => K): Map<K, T[]> {
  return items.reduce((map, item) => {
    const key = keyFn(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
    return map;
  }, new Map<K, T[]>());
}

type SortOrder = 'asc' | 'desc';

function getTranscribeJobMonths(
  transcribeJobs: TranscribeJob[],
  prioritizeEmployeeLastName: boolean,
  sortOrder: SortOrder = 'asc',
  i18n: any,
): TranscribeJobMonth[] {
  const monthsMap = mapByKey(transcribeJobs, (transcribeJob) =>
    transcribeJob.date.substring(0, 7),
  );
  const months: TranscribeJobMonth[] = [];
  monthsMap.forEach((transcribeJobs, month) => {
    const departments = getTranscribeJobDepartments(
      transcribeJobs,
      prioritizeEmployeeLastName,
      sortOrder,
      i18n,
    );

    months.push({
      month,
      departments,
      collapsed: false,
    });
  });
  // Sort months by date
  months.sort((a, b) => {
    const comparison = a.month.localeCompare(b.month);
    return sortOrder === 'desc' ? -comparison : comparison;
  });
  return months;
}

function getTranscribeJobDepartments(
  transcribeJobs: TranscribeJob[],
  prioritizeEmployeeLastName: boolean,
  sortOrder: SortOrder = 'asc',
  i18n: any,
): TranscribeJobDepartment[] {
  const departmentsMap: Map<
    string,
    {
      department: JobDepartment;
      organisation: { id: string; name: string };
      transcribeJobs: TranscribeJob[];
    }
  > = new Map();
  transcribeJobs.forEach((transcribeJob) => {
    if (!departmentsMap.has(transcribeJob.department.id)) {
      departmentsMap.set(transcribeJob.department.id, {
        department: transcribeJob.department,
        organisation: transcribeJob.organisation,
        transcribeJobs: [],
      });
    }
    departmentsMap
      .get(transcribeJob.department.id)
      ?.transcribeJobs.push(transcribeJob);
  });
  const departments: TranscribeJobDepartment[] = [];
  departmentsMap.forEach((mappedData, departmentId) => {
    departments.push({
      department: mappedData.department,
      organisation: mappedData.organisation,
      users: getTranscribeJobUsers(
        mappedData.transcribeJobs,
        prioritizeEmployeeLastName,
        sortOrder,
        i18n,
      ),
      collapsed: false,
    });
  });
  return departments;
}

function getTranscribeJobUsers(
  transcribeJobs: TranscribeJob[],
  prioritizeEmployeeLastName: boolean,
  sortOrder: SortOrder = 'asc',
  i18n: any,
): TranscribeJobUser[] {
  const usersGrouped: Map<
    string,
    {
      groupKey: string;
      userId?: string;
      personalData: Invitee['personalData'];
      customer: TranscribeJob['customer'];
      transcribeJobs: TranscribeJob[];
    }
  > = new Map();
  transcribeJobs.forEach((transcribeJob) => {
    const userKey = groupTranscribeUserKey(
      transcribeJob.invitee.userId
        ? {
            userId: transcribeJob.invitee.userId,
            customerId: transcribeJob.invitee.customer.id,
          }
        : {
            firstName: transcribeJob.invitee.personalData.firstName,
            lastName: transcribeJob.invitee.personalData.lastName,
            email: transcribeJob.invitee.personalData.email,
          },
    );
    if (!usersGrouped.has(userKey)) {
      usersGrouped.set(userKey, {
        groupKey: userKey,
        userId: transcribeJob.invitee.userId,
        personalData: transcribeJob.invitee.personalData,
        customer: transcribeJob.invitee.customer,
        transcribeJobs: [],
      });
    }
    usersGrouped.get(userKey)!.transcribeJobs.push(transcribeJob);
  });
  const users: TranscribeJobUser[] = [];
  usersGrouped.forEach((mappedData, userKey) => {
    // Sort jobs by date
    const sortedJobs = [...mappedData.transcribeJobs].sort((a, b) => {
      const comparison = a.date.localeCompare(b.date);
      return sortOrder === 'desc' ? -comparison : comparison;
    });
    // Map jobs with bookedAtInfo and cancelledAtInfo
    const jobs = sortedJobs.map((transcribeJob) => {
      const invitee = transcribeJob.invitee;
      const timestamp =
        (invitee.bookedAt || invitee.updatedAt || invitee.createdAt) * 1000;
      const date = new Date(timestamp);
      const updatedAt = format(date, 'dd.MM.yyyy HH:mm', { locale: de });
      const distance = intlFormatDistance(date, new Date(), { locale: 'de' });

      const bookedAtInfo =
        invitee.status === InviteeStatusEnum.BOOKED
          ? replaceTranslationVariables(i18n.bookedAtWithDate, {
              date: updatedAt,
              distance: distance,
            })
          : undefined;

      const cancelledAtInfo =
        invitee.status === InviteeStatusEnum.CANCELED ||
        invitee.status === InviteeStatusEnum.TRANSFERRED
          ? replaceTranslationVariables(i18n.cancelledAtWithDate, {
              date: updatedAt,
              distance: distance,
            })
          : undefined;

      return {
        transcribeJob,
        bookedAtInfo,
        cancelledAtInfo,
      };
    });
    // Create displayedName based on customer sort config
    const displayedName = prioritizeEmployeeLastName
      ? `${mappedData.personalData.lastName} ${mappedData.personalData.firstName}`
      : `${mappedData.personalData.firstName} ${mappedData.personalData.lastName}`;
    users.push({
      groupKey: mappedData.groupKey,
      userId: mappedData.userId,
      personalData: mappedData.personalData,
      customer: mappedData.customer,
      jobs,
      displayedName,
      poolerType: mappedData.personalData.poolerType,
    });
  });
  // Sort users by displayedName
  users.sort((a, b) => a.displayedName.localeCompare(b.displayedName));
  return users;
}

@Component({
  selector: 'admin-transcribe-job-list',
  templateUrl: 'transcribe-job-list.component.html',
  styleUrls: ['transcribe-job-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranscribeJobListComponent implements OnInit, OnDestroy {
  destroyed$ = new Subject<void>();
  i18n = this.translationService.getTranslations();
  jobTypeEnum = JobTypeEnum;
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  dispoCustomer$ = this.customerService.readyDispoCustomer$;
  transcribingJobIds = new Set<string>();

  // Filter infrastructure
  readonly dateRangeChips: string[] = [
    'ALL',
    'TODAY',
    'TOMORROW',
    'MONTH',
    'NEXT_MONTH',
    'CUSTOM',
  ];
  selectedRange$ = new BehaviorSubject<string>('');
  rangeParam$ = queryParam$(this.route, 'range');
  departmentIdParam$ = queryParam$(this.route, 'departmentId');
  shiftParam$ = queryParam$(this.route, 'shift');
  certificateParam$ = queryParam$(this.route, 'certificates');
  jobTypeParam$ = queryParam$(this.route, 'jobType');

  searchTypeParam$ = queryParam$(this.route, 'searchType');
  searchType$ = this.searchTypeParam$.pipe(
    map((param) => normalizeTranscribeJobListSearchType(param)),
    distinctUntilChanged(),
    shareReplay(1),
  );
  searchText$ = new BehaviorSubject<string>('');
  searchTextParam$ = queryParam$(this.route, 'searchText');
  showByTranscribedAtDateParam$ = queryParam$(
    this.route,
    'showByTranscribedAtDate',
  );

  // Toggle for showing by transcribed at date
  showByTranscribedAtDate$ = this.showByTranscribedAtDateParam$.pipe(
    map((param) => param !== 'false'),
    distinctUntilChanged(),
    shareReplay(1),
  );

  jobsSearchOptions$ = new BehaviorSubject<SearchTypes[]>([
    'ALL',
    'DESCRIPTION',
    'USER',
    'BOOKED_USER',
    'CANCELED_USER',
    'SHIFT',
  ]);

  shiftQuery$ = combineLatest([this.shiftParam$]).pipe(
    map(([shiftParam]) => {
      if (!shiftParam || shiftParam === 'ALL') {
        return undefined;
      }
      const shifts = shiftParam
        .split(',')
        .map((shift) => ShiftEnum[shift as keyof typeof ShiftEnum]);
      return {
        'shifts.shift': { $in: shifts },
      };
    }),
  );

  departmentQuery$ = this.departmentIdParam$.pipe(
    map((departmentId) => {
      if (!departmentId) {
        return undefined;
      }
      return {
        'department.id': departmentId,
      };
    }),
  );

  certificateQuery$ = this.certificateParam$.pipe(
    map((certificate) => {
      if (!certificate) {
        return undefined;
      }
      const certificateIds = certificate.split(',');
      return {
        'certificates.id': { $in: certificateIds },
      };
    }),
  );

  jobTypeQuery$ = combineLatest([this.jobTypeParam$]).pipe(
    map(([jobTypeParam]) => {
      if (!jobTypeParam || jobTypeParam === 'ALL') {
        return undefined;
      }
      const jobTypes = jobTypeParam
        .split(',')
        .map((jobType) => JobTypeEnum[jobType as keyof typeof JobTypeEnum])
        .filter((jobType) => jobType !== undefined);
      return { jobType: { $in: jobTypes } };
    }),
  );

  openTab: TranscribeJobListTab = {
    label: 'transcribe',
    value: 'open',
    svgIcon: 'board-mission-transfer-arrow',
  };

  doneTab: TranscribeJobListTab = {
    label: 'done',
    value: 'done',
    svgIcon: 'check-circle-outline',
  };

  tabs$: Observable<TranscribeJobListTab[]> = new BehaviorSubject([
    this.openTab,
    this.doneTab,
  ]).pipe(shareReplay(1));

  filtersCollapsed$ = new BehaviorSubject<boolean>(
    localStorage.getItem('transcribeJobListFiltersCollapsed') === 'true',
  );

  filterCounts$ = combineLatest([
    this.rangeParam$,
    this.departmentIdParam$,
    this.shiftParam$,
    this.certificateParam$,
    this.jobTypeParam$,
    this.searchType$,
    this.searchTextParam$,
  ]).pipe(
    map(
      ([
        range,
        departmentId,
        shift,
        certificate,
        jobType,
        searchType,
        searchText,
      ]) => {
        let count = 0;
        if (range && range !== 'ALL') {
          count++;
        }
        if (departmentId) {
          count++;
        }
        if (shift && shift !== 'ALL') {
          count++;
        }
        if (certificate) {
          count++;
        }
        if (jobType && jobType !== 'ALL') {
          count++;
        }
        if (
          searchType &&
          searchType !== TranscribeJobListSearchInFieldEnum.ALL &&
          searchText
        ) {
          count++;
        }
        return count;
      },
    ),
    distinctUntilChanged(),
    shareReplay(1),
  );

  tabParam$: Observable<string> = queryParam$(this.route, 'tab');
  activeTabId$: Observable<string> = this.tabParam$.pipe(
    map((tabParam) => tabParam || 'open'),
    distinctUntilChanged(),
    shareReplay(1),
  );

  loggedInUser$ = this.userService.readyDispo$;

  canUpdateTranscribeJobList$ =
    this.permissionsService.canUpdateTranscribeJobList$;

  prioritizeEmployeeLastName$: Observable<boolean> = this.dispoCustomer$.pipe(
    map((customer) => customer?.prioritizeEmployeeLastName ?? false),
    distinctUntilChanged(),
    shareReplay(1),
  );

  rangeQuery$ = this.rangeParam$.pipe(map((range) => getJobsRangeQuery(range)));

  searchQuery$ = combineLatest([this.searchType$, this.searchTextParam$]).pipe(
    map(([searchType, searchText]) => {
      return getTranscribeJobSearchQuery(
        searchText || '',
        searchType,
        this.i18n,
      );
    }),
  );

  transcribeJobsQuery$ = combineLatest([
    combineLatest([
      this.rangeQuery$,
      this.departmentQuery$,
      this.shiftQuery$,
      this.certificateQuery$,
      this.jobTypeQuery$,
      this.searchQuery$,
    ]).pipe(debounceTime(5)),
    this.activeTabId$,
  ]).pipe(
    map(
      ([
        [
          rangeQuery,
          departmentQuery,
          shiftQuery,
          certificateQuery,
          jobTypeQuery,
          searchQuery,
        ],
        tabId,
      ]) => {
        const $and: Mongo.Selector<TranscribeJob>[] = [];

        if (rangeQuery) {
          $and.push(rangeQuery);
        }

        // Filter by tab (transcribeStatus)
        if (tabId === 'open') {
          $and.push({
            transcribedAt: { $exists: false },
          });
        } else if (tabId === 'done') {
          $and.push({
            transcribedAt: { $exists: true },
          });
        }

        if (departmentQuery) {
          $and.push(departmentQuery);
        }

        if (shiftQuery) {
          $and.push(shiftQuery);
        }

        if (certificateQuery) {
          $and.push(certificateQuery);
        }

        if (jobTypeQuery) {
          $and.push(jobTypeQuery);
        }

        if (searchQuery) {
          $and.push(searchQuery);
        }

        const query: Mongo.Selector<TranscribeJob> =
          $and.length > 0 ? { $and } : {};
        return { query, activeTabId: tabId };
      },
    ),
    shareReplay(1),
  );

  /** Query params for server subscription (filters only, without transcribedAt) */
  transcribeJobsQueryParamsForSubscription$ = combineLatest([
    this.rangeQuery$,
    this.departmentQuery$,
    this.shiftQuery$,
    this.certificateQuery$,
    this.jobTypeQuery$,
    this.searchQuery$,
  ]).pipe(
    debounceTime(5),
    map(
      ([
        rangeQuery,
        departmentQuery,
        shiftQuery,
        certificateQuery,
        jobTypeQuery,
        searchQuery,
      ]) => {
        const $and: Mongo.Selector<TranscribeJob>[] = [];
        if (rangeQuery) $and.push(rangeQuery);
        if (departmentQuery) $and.push(departmentQuery);
        if (shiftQuery) $and.push(shiftQuery);
        if (certificateQuery) $and.push(certificateQuery);
        if (jobTypeQuery) $and.push(jobTypeQuery);
        if (searchQuery) $and.push(searchQuery);
        return $and.length > 0 ? ({ $and } as Record<string, unknown>) : {};
      },
    ),
    shareReplay(1),
  );

  transcribeJobs$: Observable<TranscribeJobsData> = combineLatest([
    this.transcribeJobsQuery$,
    this.showByTranscribedAtDate$,
    this.activeTabId$,
  ]).pipe(
    switchMap(
      ([
        { query, activeTabId },
        showByDate,
        tabId,
      ]): Observable<TranscribeJobsData> => {
        const useDateSubscription = showByDate && tabId === 'done';

        return this.subscriptionService.transcribeJobsLoading$.pipe(
          switchMap((subscriptionLoading) =>
            TranscribeJobs.find(query, {
              sort: useDateSubscription
                ? { transcribedAt: -1 }
                : {
                    'organisation.name': 1,
                    'department.name': 1,
                  },
            }).pipe(
              map((transcribeJobs) => ({
                transcribeJobs,
                activeTabId,
                loading: subscriptionLoading,
              })),
              debounceTime(150),
              rxNgZoneRun(this.ngZone),
            ),
          ),
          startWith({
            transcribeJobs: [],
            activeTabId,
            loading: true,
          }),
        );
      },
    ),
    shareReplay(1),
  );

  months$ = combineLatest([
    this.transcribeJobs$,
    this.prioritizeEmployeeLastName$,
  ]).pipe(
    scan(
      (
        prevState: TranscribeListState | null,
        [transcribeJobsData, prioritizeEmployeeLastName],
      ) => {
        const { transcribeJobs, activeTabId, loading } = transcribeJobsData;
        const sortOrder: 'asc' | 'desc' =
          activeTabId === 'done' ? 'desc' : 'asc';
        const newMonths = getTranscribeJobMonths(
          transcribeJobs,
          prioritizeEmployeeLastName,
          sortOrder,
          this.i18n,
        );

        // Preserve collapsed state from previous months or initialize on first load
        if (prevState?.months) {
          const prevCollapsedMap = this.getCollapsedMonthsMap(prevState.months);
          this.applyMonthCollapsedState(newMonths, prevCollapsedMap);
        }

        return {
          months: newMonths,
          activeTabId,
          loading,
        };
      },
      null as {
        months: TranscribeJobMonth[];
        activeTabId: string;
        loading: boolean;
      } | null,
    ),
    filter(
      (
        state,
      ): state is {
        months: TranscribeJobMonth[];
        activeTabId: string;
        loading: boolean;
      } => state !== null,
    ),
    shareReplay(1),
  );

  getCollapsedMonthsMap(months: TranscribeJobMonth[]): MonthCollapsedStateMap {
    const collapsedMap: MonthCollapsedStateMap = new Map();

    months.forEach((month) => {
      const departmentMap = new Map<string, boolean>();

      month.departments.forEach((dept) => {
        departmentMap.set(dept.department.id, dept.collapsed);
      });

      collapsedMap.set(month.month, {
        collapsed: month.collapsed,
        departments: departmentMap,
      });
    });

    return collapsedMap;
  }

  applyMonthCollapsedState(
    newMonths: TranscribeJobMonth[],
    previousCollapsedStateMap: MonthCollapsedStateMap,
  ): void {
    newMonths.forEach((newMonth) => {
      // Restore month and department collapsed states using Map
      const monthState = previousCollapsedStateMap.get(newMonth.month);
      if (monthState) {
        // Restore month collapsed state
        newMonth.collapsed = monthState.collapsed;

        // Restore department collapsed states
        newMonth.departments.forEach((newDept) => {
          const deptCollapsed = monthState.departments.get(
            newDept.department.id,
          );
          if (deptCollapsed !== undefined) {
            newDept.collapsed = deptCollapsed;
          }
        });
      }
    });
  }

  toggleMonthCollapsed(
    monthItem: TranscribeJobMonth,
    collapsed: boolean,
  ): void {
    monthItem.collapsed = collapsed;
    this.cdr.markForCheck();
  }

  toggleDepartmentCollapsed(
    department: TranscribeJobDepartment,
    collapsed: boolean,
  ): void {
    department.collapsed = collapsed;
    this.cdr.markForCheck();
  }

  disableFilterSave$ = this.dispoCustomer$.pipe(
    map((customer) => customer?.disableDispoFilterSave),
    distinctUntilChanged(),
  );

  userDefaultFilters$ = combineLatest([
    this.activeTabId$,
    this.loggedInUser$,
  ]).pipe(
    map(([tabId, user]) => {
      return user?.transcribeJobListFilters?.[tabId];
    }),
    shareReplay(1),
    rxDistinctJSONStringify(),
  );

  initDefaultFiltersEffect$ = combineLatest([
    this.userDefaultFilters$,
    this.route.queryParams,
  ]).pipe(
    take(1),
    tap(([defaultFilters, queryParams]) => {
      const hasFilterParams =
        queryParams.departmentId ||
        queryParams.shift ||
        queryParams.certificates ||
        queryParams.jobType ||
        queryParams.range ||
        queryParams.searchType ||
        queryParams.searchText ||
        queryParams.showByTranscribedAtDate;
      // dont load default filter params when coming from a link with existing filters
      if (hasFilterParams) {
        return;
      }
      this.mergeQueryParams({
        departmentId: defaultFilters?.departmentId || null,
        shift: defaultFilters?.shift || null,
        certificates: defaultFilters?.certificates || null,
        jobType: defaultFilters?.jobType || null,
        range: defaultFilters?.range || null,
        searchType: normalizeTranscribeJobListSavedSearchType(
          defaultFilters?.searchType,
        ),
        searchText: normalizeTranscribeJobListSavedSearchText(
          defaultFilters?.searchType,
          defaultFilters?.searchText,
        ),
        showByTranscribedAtDate:
          defaultFilters?.showByTranscribedAtDate === false
            ? 'false'
            : typeof defaultFilters?.showByTranscribedAtDate === 'string' &&
              defaultFilters.showByTranscribedAtDate === 'false'
            ? 'false'
            : null,
      });
    }),
  );

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private translationService: TranslationService,
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private router: Router,
    private subscriptionService: SubscriptionService,
    private userService: UserService,
    private toastService: ToastService,
    private permissionsService: PermissionsService,
  ) {
    combineLatest([
      this.userService.role$,
      this.permissionsService.canReadTranscribeJobList$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([role, canRead]) => {
        if (role !== RoleEnum.DISPO || !canRead) {
          this.router.navigate(['/']).then();
        }
      });
  }

  ngOnInit(): void {
    // cant be in constructor to not interrupt the initial navigation which would cause the sidenav to not get the navigation event
    this.initDefaultFiltersEffect$.subscribe();

    this.subscriptionService.organisations.ready$
      .pipe(takeUntil(this.destroyed$))
      .subscribe();

    // Initialize persistent jobs subscription
    this.subscriptionService.initPersistentJobsSubscription();

    // Subscribe to transcribe jobs (default subscription)

    // Subscribe to transcribe jobs with query params so limit applies to filtered jobs
    combineLatest([
      this.activeTabId$,
      this.showByTranscribedAtDate$,
      this.transcribeJobsQueryParamsForSubscription$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([tabId, showByDate, queryParams]) => {
        if (tabId === 'open') {
          this.subscriptionService.setTranscribeJobsSubscription(
            'openTranscribeJobs',
            queryParams,
          );
        } else if (showByDate) {
          this.subscriptionService.setTranscribeJobsSubscription(
            'transcribeJobsByDate',
            queryParams,
          );
        } else {
          this.subscriptionService.setTranscribeJobsSubscription(
            'transcribeJobs',
            queryParams,
          );
        }
      });

    // Initialize selectedRange$ from query params
    this.rangeParam$.pipe(takeUntil(this.destroyed$)).subscribe((range) => {
      if (range) {
        const splitRange = range.split(',');
        this.selectedRange$.next(splitRange[0]);
      } else {
        this.selectedRange$.next('ALL');
      }
      this.cdr.markForCheck();
    });

    // Keep searchText$ in sync with searchTextParam$
    this.searchTextParam$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((searchText) => {
        this.searchText$.next(searchText || '');
        this.cdr.markForCheck();
      });

    this.customerService.readyDispoCustomer$
      .pipe(take(1), takeUntil(this.destroyed$), rxNgZoneRun(this.ngZone))
      .subscribe((customer) => {
        this.customer$.next(customer);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  toggleFilters(): void {
    this.filtersCollapsed$.next(!this.filtersCollapsed$.value);
    localStorage.setItem(
      'transcribeJobListFiltersCollapsed',
      this.filtersCollapsed$.value.toString(),
    );
    this.cdr.markForCheck();
  }

  switchTab(tabId: string) {
    this.loggedInUser$.pipe(take(1)).subscribe((user) => {
      const defaultFilters = user?.transcribeJobListFilters?.[tabId];
      this.mergeQueryParams({
        tab: tabId,
        departmentId: defaultFilters?.departmentId || null,
        shift: defaultFilters?.shift || null,
        certificates: defaultFilters?.certificates || null,
        jobType: defaultFilters?.jobType || null,
        range: defaultFilters?.range || null,
        searchType: normalizeTranscribeJobListSavedSearchType(
          defaultFilters?.searchType,
        ),
        searchText: normalizeTranscribeJobListSavedSearchText(
          defaultFilters?.searchType,
          defaultFilters?.searchText,
        ),
        showByTranscribedAtDate:
          defaultFilters?.showByTranscribedAtDate === false
            ? 'false'
            : typeof defaultFilters?.showByTranscribedAtDate === 'string' &&
              defaultFilters.showByTranscribedAtDate === 'false'
            ? 'false'
            : null,
      });
    });
  }

  mergeQueryParams(queryParams: Params) {
    this.router
      .navigate([], {
        queryParamsHandling: 'merge',
        queryParams,
      })
      .then();
  }

  resetFilters(): void {
    this.mergeQueryParams({
      departmentId: null,
      shift: null,
      certificates: null,
      jobType: null,
      range: null,
      searchType: null,
      searchText: null,
      showByTranscribedAtDate: null,
    });
  }

  saveTranscribeJobListFilters() {
    this.activeTabId$.pipe(take(1)).subscribe((tabId: string) => {
      const queryParams = this.route.snapshot.queryParams;

      Meteor.call(
        'saveTranscribeJobListFilters',
        tabId,
        {
          departmentId: queryParams['departmentId'] || null,
          shift: queryParams['shift'] || null,
          certificates: queryParams['certificates'] || null,
          jobType: queryParams['jobType'] || null,
          range: queryParams['range'] || null,
          searchType: queryParams['searchType'] || null,
          searchText:
            queryParams['searchText'] || this.searchText$.value || null,
          showByTranscribedAtDate:
            queryParams['showByTranscribedAtDate'] === 'false' ? 'false' : null,
        },
        (error: any) => {
          if (error) {
            console.error('Error saving transcribe job list filters:', error);
            this.toastService.add({
              type: 'ERROR',
              message: 'Failed to save filters',
              timeout: 5000,
            });
          } else {
            this.toastService.add({
              type: 'SUCCESS',
              message: 'savedDefaultFilter',
              timeout: 5000,
            });
          }
        },
      );
    });
  }

  selectSearchType(searchType: SearchTypes) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        searchType: searchType && searchType !== 'ALL' ? searchType : undefined,
      },
      queryParamsHandling: 'merge',
    });
  }

  enableSearch() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        searchText: this.searchText$.value || undefined,
      },
      queryParamsHandling: 'merge',
    });
  }

  trackByMonth(_: number, monthItem: { month: string }): string {
    return monthItem.month;
  }

  trackByDepartmentId(_: number, dept: { department: JobDepartment }): string {
    return dept.department.id;
  }

  trackByUserId(_: number, user: { groupKey: string }): string | undefined {
    return user.groupKey;
  }

  trackByTranscribeJobId(
    _: number,
    jobItem: { transcribeJob: TranscribeJob },
  ): string {
    return jobItem.transcribeJob._id;
  }

  async transcribeJob(transcribeJobId: string) {
    // Prevent double clicks - if already processing, return early
    if (this.transcribingJobIds.has(transcribeJobId)) {
      return;
    }

    // Mark as processing
    this.transcribingJobIds.add(transcribeJobId);
    this.cdr.markForCheck();

    Meteor.call(
      'setTranscribeJobTranscribedAt',
      transcribeJobId,
      (error: Meteor.Error) => {
        if (error) {
          console.error('Error transcribing job:', error);
          this.toastService.add({
            type: 'ERROR',
            message: error.message || 'Failed to transcribe job',
            timeout: 5000,
          });
        }

        // Delay removal of lock by 1 second to keep button disabled
        setTimeout(() => {
          this.transcribingJobIds.delete(transcribeJobId);
          this.cdr.markForCheck();
        }, 1000);
      },
    );
  }

  inviteeStatusEnum = InviteeStatusEnum;

  // Limit for date view
  readonly DATE_VIEW_LIMIT = 100;
  dateViewLimit$ = new BehaviorSubject<number>(this.DATE_VIEW_LIMIT);

  // Observable for transcribe jobs sorted by transcribedAt date
  transcribeJobsByDate$: Observable<
    Array<{
      transcribeJob: TranscribeJob;
      bookedAtInfo?: string;
      cancelledAtInfo?: string;
      transcribedAtInfo?: string;
    }>
  > = combineLatest([
    this.transcribeJobs$,
    this.activeTabId$,
    this.showByTranscribedAtDate$,
    this.dateViewLimit$,
  ]).pipe(
    map(([transcribeJobsData, activeTabId, showByDate, limit]) => {
      if (!showByDate || activeTabId !== 'done') {
        return [];
      }

      const { transcribeJobs } = transcribeJobsData;

      // Filter only transcribed jobs and sort by transcribedAt date (descending)
      const sortedJobs = transcribeJobs
        .filter((job) => job.transcribedAt)
        .sort((a, b) => {
          const dateA = a.transcribedAt?.getTime() || 0;
          const dateB = b.transcribedAt?.getTime() || 0;
          return dateB - dateA; // Descending order (newest first)
        })
        .slice(0, limit)
        .map((transcribeJob) => {
          const invitee = transcribeJob.invitee;
          const timestamp =
            (invitee.bookedAt || invitee.updatedAt || invitee.createdAt) * 1000;
          const date = new Date(timestamp);
          const updatedAt = format(date, 'dd.MM.yyyy HH:mm', { locale: de });
          const distance = intlFormatDistance(date, new Date(), {
            locale: 'de',
          });

          const bookedAtInfo =
            invitee.status === InviteeStatusEnum.BOOKED
              ? replaceTranslationVariables(this.i18n.bookedAtWithDate, {
                  date: updatedAt,
                  distance: distance,
                })
              : undefined;

          const cancelledAtInfo =
            invitee.status === InviteeStatusEnum.CANCELED ||
            invitee.status === InviteeStatusEnum.TRANSFERRED
              ? replaceTranslationVariables(this.i18n.cancelledAtWithDate, {
                  date: updatedAt,
                  distance: distance,
                })
              : undefined;

          // Calculate transcribedAt info
          let transcribedAtInfo: string | undefined;
          if (transcribeJob.transcribedAt) {
            const transcribedDate = new Date(transcribeJob.transcribedAt);
            const transcribedAtFormatted = format(
              transcribedDate,
              'dd.MM.yyyy HH:mm',
              { locale: de },
            );
            const transcribedDistance = intlFormatDistance(
              transcribedDate,
              new Date(),
              { locale: 'de' },
            );
            const baseString = replaceTranslationVariables(
              this.i18n.transcribedAtWithDate,
              {
                date: transcribedAtFormatted,
                distance: transcribedDistance,
              },
            );
            // Add "von" (by) if transcribedBy exists
            if (transcribeJob.transcribedBy?.name) {
              transcribedAtInfo = `${baseString} von ${transcribeJob.transcribedBy.name}`;
            } else {
              transcribedAtInfo = baseString;
            }
          }

          return {
            transcribeJob,
            bookedAtInfo,
            cancelledAtInfo,
            transcribedAtInfo,
          };
        });

      return sortedJobs;
    }),
    map((sortedJobs) => {
      return sortedJobs.slice(0, 999);
    }),
    shareReplay(1),
  );

  hasMoreJobsToLoad$ = combineLatest([
    this.transcribeJobs$,
    this.dateViewLimit$,
  ]).pipe(
    map(([transcribeJobsData, limit]) => {
      const { transcribeJobs } = transcribeJobsData;
      const transcribedJobs = transcribeJobs.filter((job) => job.transcribedAt);
      return transcribedJobs.length > limit;
    }),
    shareReplay(1),
  );

  // Check if we're showing the maximum (1k) transcribe jobs
  // Only show hint when we're at the limit AND can't load more
  showLimitHint$ = combineLatest([
    this.transcribeJobs$,
    this.hasMoreJobsToLoad$,
    this.showByTranscribedAtDate$,
  ]).pipe(
    map(([transcribeJobsData, hasMore, showByDate]) => {
      const { transcribeJobs } = transcribeJobsData;
      // Show hint only when at 1k limit AND no more jobs to load
      return transcribeJobs.length >= 990 && (!showByDate || !hasMore);
    }),
    shareReplay(1),
  );

  toggleShowByTranscribedAtDate(): void {
    this.showByTranscribedAtDate$.pipe(take(1)).subscribe((currentValue) => {
      const newValue = !currentValue;
      this.mergeQueryParams({
        showByTranscribedAtDate: newValue ? null : 'false',
      });
      // Reset limit when toggling off
      if (!newValue) {
        this.dateViewLimit$.next(this.DATE_VIEW_LIMIT);
      }
    });
  }

  loadMoreJobs(): void {
    this.dateViewLimit$.next(this.dateViewLimit$.value + this.DATE_VIEW_LIMIT);
    this.cdr.markForCheck();
  }
}
